const HAVE_METADATA = 1;

export interface AudioEngineEvents {
    onLoad(): void;
    onPlay(): void;
    onPause(): void;
    onEnd(): void;
    onLoadError(error: unknown): void;
    onPlayError(error: unknown): void;
}

export interface LoadOptions {
    src: string;
    volume: number;
    startFrom?: number;
}

export class AudioEngine {
    private audio: HTMLAudioElement | null = null;
    private events: AudioEngineEvents | null = null;
    private pendingSeek: number | null = null;
    private loadFired = false;

    get loaded(): boolean {
        return !!this.audio && !!this.audio.src && this.audio.readyState >= HAVE_METADATA;
    }

    load(options: LoadOptions, events: AudioEngineEvents): void {
        const audio = this.ensureAudio();

        this.events = events;
        this.loadFired = false;
        this.pendingSeek = options.startFrom && options.startFrom > 0 ? options.startFrom : null;

        audio.volume = options.volume;
        audio.src = options.src;
        audio.load();
    }

    play(): void {
        if (!this.audio) return;

        this.applyPendingSeek();

        const playback = this.audio.play();

        if (playback) {
            playback.catch(error => this.events?.onPlayError(error));
        }
    }

    pause(): void {
        this.audio?.pause();
    }

    stop(): void {
        if (!this.audio) return;

        this.audio.pause();
        this.audio.currentTime = 0;
    }

    getPosition(): number {
        if (!this.audio) return 0;

        const position = this.audio.currentTime;

        return Number.isFinite(position) ? Math.round(position) : 0;
    }

    seekTo(seconds: number): boolean {
        if (!this.audio) return false;

        if (this.audio.readyState >= HAVE_METADATA) {
            this.audio.currentTime = seconds;
            return true;
        }

        this.pendingSeek = seconds;
        return false;
    }

    setVolume(volume: number): void {
        if (this.audio) this.audio.volume = volume;
    }

    unload(): void {
        if (!this.audio) return;

        this.audio.pause();
        this.audio.removeAttribute("src");
        this.audio.load();

        this.events = null;
        this.pendingSeek = null;
        this.loadFired = false;
    }

    private ensureAudio(): HTMLAudioElement {
        if (this.audio) return this.audio;

        const audio = new Audio();
        audio.preload = "auto";

        audio.addEventListener("loadedmetadata", () => {
            this.applyPendingSeek();
        });

        audio.addEventListener("canplaythrough", () => {
            if (this.loadFired) return;
            this.loadFired = true;
            this.events?.onLoad();
        });

        audio.addEventListener("play", () => {
            this.applyPendingSeek();
            this.events?.onPlay();
        });

        audio.addEventListener("pause", () => {
            this.events?.onPause();
        });

        audio.addEventListener("ended", () => {
            this.events?.onEnd();
        });

        audio.addEventListener("error", () => {
            this.events?.onLoadError(new Error(audio.error?.message ?? "Audio load failed"));
        });

        return this.audio = audio;
    }

    private applyPendingSeek(): void {
        if (this.pendingSeek === null || !this.audio) return;
        if (this.audio.readyState < HAVE_METADATA) return;

        this.audio.currentTime = this.pendingSeek;
        this.pendingSeek = null;
    }
}
